import { useState, useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Heart, Star, Settings, LayoutDashboard } from "lucide-react";
import ihLogo from "@/assets/ih-logo.png";
import { useLanguage } from "@/contexts/LanguageContext";
import { AuthContext } from "@/contexts/AuthContext";
import { AUTH_ENABLED } from "@/config/features";
import { scrollToSection } from "@/lib/scrollToSection";
import { useUserFavorites } from "@/hooks/useUserFavorites";
import { getNationByCode } from "@/data/hockeyNations";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import LanguageSelector from "./LanguageSelector";
import AuthModal from "./AuthModal";
import SubscriptionModal from "./SubscriptionModal";
import NotificationBell from "./NotificationBell";
import FavoriteTeamsModal from "./FavoriteTeamsModal";
import FavoritePlayersModal from "./FavoritePlayersModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle navigation for hash links - works across pages
  const handleHashNavigation = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    const hash = href.includes("#") ? href.split("#")[1] : null;
    
    if (!hash) {
      navigate(href);
      return;
    }

    // If we're on the homepage, just scroll
    if (location.pathname === "/") {
      scrollToSection(hash);
    } else {
      // Navigate to homepage WITH hash so AuthenticatedRedirect doesn't intercept
      navigate(`/#${hash}`);
      setTimeout(() => scrollToSection(hash, { maxAttempts: 40, pollInterval: 100 }), 150);
    }
  };

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const { t } = useLanguage();
  const { hasFavorites, hasFavoritePlayers } = useUserFavorites();
  // Use useContext directly to avoid throwing when context is undefined during HMR
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const subscription = authContext?.subscription ?? { subscribed: false, tier: null, isTrial: false };

  // Fetch user's favorite nation
  const { data: userProfile } = useQuery({
    queryKey: ["userProfile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("favorite_nation")
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const favoriteNation = getNationByCode(userProfile?.favorite_nation ?? null);
  const signOut = authContext?.signOut ?? (async () => {});

  const navLinks = [
    { name: t.nav.scores, href: "/#live-games", isRoute: true },
    { name: t.nav.standings, href: "/#standings", isRoute: true },
    { name: t.nav.international, href: "/#international-tournaments", isRoute: true },
    { name: t.nav.schedule, href: "/schedule", isRoute: true },
    
    { name: t.navExtra.pricing, href: "/pricing", isRoute: true },
  ];

  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 transition-all duration-300 ${isScrolled ? "h-12" : "h-16"}`}>
        <div className="container mx-auto px-6 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo */}
            <Link to="/#hero" className="flex items-center gap-2">
              <img 
                src={ihLogo} 
                alt="IceHub" 
                className={`rounded-lg transition-all duration-300 ${isScrolled ? "w-10 h-10" : "w-12 h-12"}`}
              />
              <span className={`font-display font-bold text-foreground transition-all duration-300 ${isScrolled ? "text-lg" : "text-xl"}`}>
                Ice<span className="text-primary">Hub</span>
              </span>
            </Link>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => {
                const hasHash = link.href.includes("#");
                return hasHash ? (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleHashNavigation(e, link.href)}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-2">
              {user && (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
                  title={t.navExtra.myPage}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span className="hidden lg:inline">{t.navExtra.myPage}</span>
                </Link>
              )}
              <button
                onClick={() => setShowFavoritesModal(true)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  hasFavorites
                    ? "text-primary bg-primary/10 hover:bg-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
                title={t.navExtra.myTeams}
              >
                <Heart className={`w-4 h-4 ${hasFavorites ? "fill-primary" : ""}`} />
                <span className="hidden lg:inline">{t.navExtra.myTeams}</span>
              </button>
              <button
                onClick={() => setShowPlayersModal(true)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  hasFavoritePlayers
                    ? "text-primary bg-primary/10 hover:bg-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
                title={t.navExtra.myPlayers}
              >
                <Star className={`w-4 h-4 ${hasFavoritePlayers ? "fill-primary" : ""}`} />
                <span className="hidden lg:inline">{t.navExtra.myPlayers}</span>
              </button>
              <NotificationBell />
              <LanguageSelector />
              {user ? (
                <>
                  <button
                    onClick={() => setShowSubscriptionModal(true)}
                    className="px-4 py-2 bg-primary text-primary-foreground font-medium text-sm rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    {subscription.subscribed
                      ? `${subscription.tier?.toUpperCase() || "PRO"}${subscription.isTrial ? " (Trial)" : ""}`
                      : t.nav.subscribe}
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium px-3 py-2">
                        {favoriteNation && (
                          <span className="text-base" title={favoriteNation.name}>
                            {favoriteNation.flag}
                          </span>
                        )}
                        <User className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to="/account" className="flex items-center">
                          <Settings className="w-4 h-4 mr-2" />
                          {t.navExtra.account}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleSignOut}>
                        <LogOut className="w-4 h-4 mr-2" />
                        {t.navExtra.signOut}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : AUTH_ENABLED ? (
                <>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium px-3 py-2"
                  >
                    {t.nav.signIn}
                  </button>
                  <button
                    onClick={() => setShowSubscriptionModal(true)}
                    className="px-4 py-2 bg-primary text-primary-foreground font-medium text-sm rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    {t.nav.subscribe}
                  </button>
                </>
              ) : null}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSelector />
              <button
                className="text-foreground"
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu - positioned outside nav to avoid height conflicts */}
      {isOpen && (
        <div className={`fixed left-0 right-0 z-40 md:hidden bg-background backdrop-blur-xl border-b border-border/50 animate-fade-in ${isScrolled ? "top-12" : "top-16"}`}>
          <div className="container mx-auto px-6 py-4">
            {navLinks.map((link) => {
              const hasHash = link.href.includes("#");
              return hasHash ? (
                <a
                  key={link.name}
                  href={link.href}
                  className="block py-3 text-muted-foreground hover:text-foreground transition-colors font-medium"
                  onClick={(e) => {
                    handleHashNavigation(e, link.href);
                    setIsOpen(false);
                  }}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block py-3 text-muted-foreground hover:text-foreground transition-colors font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              );
            })}
            {/* My Teams button for mobile */}
            <button
              onClick={() => {
                setShowFavoritesModal(true);
                setIsOpen(false);
              }}
              className={`flex items-center gap-2 py-3 font-medium transition-colors ${
                hasFavorites ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Heart className={`w-4 h-4 ${hasFavorites ? "fill-primary" : ""}`} />
              {t.navExtra.myTeams}
            </button>
            {/* My Players button for mobile */}
            <button
              onClick={() => {
                setShowPlayersModal(true);
                setIsOpen(false);
              }}
              className={`flex items-center gap-2 py-3 font-medium transition-colors ${
                hasFavoritePlayers ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Star className={`w-4 h-4 ${hasFavoritePlayers ? "fill-primary" : ""}`} />
              {t.navExtra.myPlayers}
            </button>
            <div className="flex flex-col gap-2 pt-4 mt-4 border-t border-border/50">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 py-2 text-muted-foreground hover:text-foreground transition-colors font-medium"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {t.navExtra.myPage}
                  </Link>
                  <Link
                    to="/account"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 py-2 text-muted-foreground hover:text-foreground transition-colors font-medium"
                  >
                    <Settings className="w-4 h-4" />
                    {t.navExtra.account}
                  </Link>
                  <button
                    onClick={() => {
                      setShowSubscriptionModal(true);
                      setIsOpen(false);
                    }}
                    className="py-2 bg-primary text-primary-foreground font-medium rounded-lg"
                  >
                    {subscription.subscribed ? t.navExtra.managePlan : t.nav.subscribe}
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="py-2 text-muted-foreground hover:text-foreground transition-colors font-medium"
                  >
                    {t.navExtra.signOut}
                  </button>
                </>
              ) : AUTH_ENABLED ? (
                <>
                  <button
                    onClick={() => {
                      setShowAuthModal(true);
                      setIsOpen(false);
                    }}
                    className="flex-1 py-2 text-muted-foreground hover:text-foreground transition-colors font-medium"
                  >
                    {t.nav.signIn}
                  </button>
                  <button
                    onClick={() => {
                      setShowSubscriptionModal(true);
                      setIsOpen(false);
                    }}
                    className="flex-1 py-2 bg-primary text-primary-foreground font-medium rounded-lg"
                  >
                    {t.nav.subscribe}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onNeedAuth={() => {
          setShowSubscriptionModal(false);
          setShowAuthModal(true);
        }}
      />
      <FavoriteTeamsModal isOpen={showFavoritesModal} onClose={() => setShowFavoritesModal(false)} />
      <FavoritePlayersModal isOpen={showPlayersModal} onClose={() => setShowPlayersModal(false)} />
    </>
  );
};

export default Navbar;
